// frontend/src/components/noel/SantaPublicChat.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Send,
  Gift,
  Sparkles,
  Trash2,
  Mic,
  MicOff,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../Api/apiConfig";
import "./SantaPublicChat.css";

const STORAGE_KEY = "chatHistory_santa_public";

// 🔑 Récupérer le token JWT
const getAuthToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

// 🔧 Supprimer les emojis avant envoi à l'IA voix
const removeEmojis = (text) => {
  return text.replace(
    /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g,
    ""
  );
};

// 🧊 État de base en cas d'erreur de statut
const initialSantaStatus = {
  isPremium: false,
  remainingFree: 0,
  maxMessagesFree: 0,
};

export default function SantaPublicChat() {
  const navigate = useNavigate();

  // 🔐 Connecté ou invité
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 🎬 Intro vidéo
  const [showIntro, setShowIntro] = useState(true);

  // 🎛 Suivi automatique (par défaut ON)
  const [autoFollow, setAutoFollow] = useState(true);

  // 🔢 Statut côté backend (limite Père Noël)
  const [santaStatus, setSantaStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState("");

  // 🎄 Compte à rebours avant Noël
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isChristmas: false,
  });

  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const hasToken = getAuthToken();

      if (hasToken && saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Erreur lecture historique:", e);
    }

    return [
      {
        sender: "ai",
        content:
          "Ho ho ho ! Bonjour mon enfant. Je suis le Père Noël. Comment t'appelles-tu ?",
      },
    ];
  });

  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechError, setSpeechError] = useState("");

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const introVideoRef = useRef(null); // ref pour la vidéo intro

  // 🔔 Popup plans quand limite atteinte
  const [showPlanModal, setShowPlanModal] = useState(false);

  // ==========================
  //  Init login / token
  // ==========================
  useEffect(() => {
    const token = getAuthToken();
    setIsLoggedIn(!!token);
  }, []);

  // ==========================
  //  Compte à rebours avant Noël
  // ==========================
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();

      let year = now.getFullYear();
      let target = new Date(`${year}-12-25T00:00:00`);
      if (now > target) {
        target = new Date(`${year + 1}-12-25T00:00:00`);
      }

      const diff = target - now;

      if (diff <= 0) {
        setCountdown({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isChristmas: true,
        });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({
        days,
        hours,
        minutes,
        seconds,
        isChristmas: false,
      });
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, []);

  // ==========================
  //  Récupérer statut Père Noël (limite backend)
  // ==========================
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setStatusLoading(true);

        const token = getAuthToken();
        console.log("🎫 Token envoyé à /santa/status :", !!token);

        const res = await apiClient.get("/santa/status", {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        console.log("🎅 Réponse /santa/status :", res.data);
        setSantaStatus(res.data);
        setStatusError("");
      } catch (err) {
        console.error("Erreur récupération statut Père Noël :", err);
        setStatusError(
          "Impossible de récupérer les informations du Père Noël pour le moment."
        );
        setSantaStatus(initialSantaStatus);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchStatus();
  }, []);

  // ==========================
  //  Sauvegarde historique (connecté uniquement)
  // ==========================
  useEffect(() => {
    if (!isLoggedIn) return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      console.error("Erreur sauvegarde historique:", e);
    }
  }, [messages, isLoggedIn]);

  // ==========================
  //  Auto scroll
  // ==========================
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (
      autoFollow ||
      (messagesEndRef.current &&
        messagesEndRef.current.offsetTop < window.innerHeight)
    ) {
      scrollToBottom();
    }
  }, [messages, loading, voiceLoading, autoFollow]);

  // ==========================
  //  Stop micro
  // ==========================
  const stopListening = () => {
    if (recognitionRef.current) {
      console.log("🛑 Arrêt du micro (stopListening)");
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  // ==========================
  //  Voix Père Noël (OpenAI TTS)
  // ==========================
  const playSantaVoice = async (text, onEndCallback) => {
    try {
      setVoiceLoading(true);
      const cleanText = removeEmojis(text);

      await new Promise((resolve) => setTimeout(resolve, 600));

      const audioRes = await apiClient.post(
        "/santa-voice/voice-openai",
        { text: cleanText },
        { responseType: "blob", withCredentials: true }
      );

      const blob = audioRes.data;

      if (blob.type && !blob.type.startsWith("audio/")) {
        console.error("Blob non audio reçu pour la voix Santa :", blob);
        return;
      }

      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);

      audio.onended = () => {
        URL.revokeObjectURL(url);
        console.log("🎧 Santa a fini de parler !");
        if (onEndCallback) onEndCallback();
      };

      await audio.play();
    } catch (err) {
      console.error("Erreur OpenAI TTS (Père Noël) :", err);
    } finally {
      setVoiceLoading(false);
    }
  };

  // ==========================
  //  Navigation vers les plans
  // ==========================
  const handleSelectPlan = (planId) => {
    if (planId === "free") {
      setShowPlanModal(false);
      navigate("/pere-noel");
    } else if (planId === "family") {
      navigate("/pere-noel/premium/famille-magique");
    } else if (planId === "reindeer") {
      navigate("/pere-noel/premium/renne-du-pere-noel");
    }
  };

  // ==========================
  //  Envoi message (centralisé)
  // ==========================
  const sendToSanta = async (userText) => {
    const text = userText.trim();
    if (!text || loading || voiceLoading) return;

    if (
      santaStatus?.remainingFree !== null &&
      !santaStatus?.isPremium &&
      santaStatus.remainingFree <= 0
    ) {
      setShowPlanModal(true);

      if (
        messages[messages.length - 1].content.indexOf(
          "Tu as utilisé tous tes messages gratuits"
        ) === -1
      ) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            content:
              "Ho ho ho… Tu as utilisé tous tes messages gratuits avec le Père Noël. Demande à un adulte de choisir un pack magique 🎄",
          },
        ]);
      }
      return;
    }

    stopListening();
    setInputText("");
    setMessages((prev) => [...prev, { sender: "user", content: text }]);
    setLoading(true);

    try {
      const token = getAuthToken();

      const response = await apiClient.post(
        "/santa/chat",
        {
          sender_id: 0,
          message: text,
        },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      console.log("🎅 Réponse /santa/chat :", response.data);

      const santaReply =
        response?.data?.reply ||
        "Ho ho ho ! Le réseau du Pôle Nord a un peu gelé, mais je t'ai bien entendu !";

      if (response?.data) {
        setSantaStatus((prev) => {
          const prevSafe = prev || {};
          const max =
            prevSafe.maxMessagesFree ?? response.data.maxMessagesFree ?? 0;
          const remaining =
            typeof response.data.remainingFree === "number"
              ? response.data.remainingFree
              : prevSafe.remainingFree;

          return {
            ...prevSafe,
            ...response.data,
            messagesUsed:
              typeof remaining === "number" && typeof max === "number"
                ? max - remaining
                : prevSafe.messagesUsed,
          };
        });
      }

      setMessages((prev) => [...prev, { sender: "ai", content: santaReply }]);

      await playSantaVoice(santaReply, () => {
        if (!autoFollow) return;

        const newRemaining =
          response?.data?.remainingFree ?? santaStatus?.remainingFree;
        const isPremium =
          response?.data?.isPremium ?? santaStatus?.isPremium ?? false;

        if (!isPremium && newRemaining === 0) {
          setShowPlanModal(true);
          return;
        }

        console.log("🎤 Santa a fini → reprise automatique de l’écoute !");
        handleToggleMic();
      });
    } catch (err) {
      console.error("Erreur API Père Noël :", err);

      if (
        err.response &&
        err.response.status === 403 &&
        err.response.data?.error === "LIMIT_REACHED"
      ) {
        const data = err.response.data;
        setSantaStatus((prev) => ({
          ...(prev || {}),
          isPremium: false,
          remainingFree: data.remainingFree ?? 0,
          maxMessagesFree:
            (prev && prev.maxMessagesFree) ?? data.maxMessagesFree,
        }));

        setMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            content:
              data.message ||
              "Ho ho ho… Tu as atteint la limite de messages gratuits avec le Père Noël 🎄",
          },
        ]);

        setShowPlanModal(true);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            content:
              "Oups ! Un lutin a trébuché sur le câble internet. Peux-tu répéter ?",
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  //  Reconnaissance vocale
  // ==========================
  const initSpeechRecognition = () => {
    if (typeof window === "undefined") return null;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechError(
        "La reconnaissance vocale n'est pas supportée sur ce navigateur."
      );
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "fr-FR";
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      console.log("🎙️ MICRO ACTIF !");
      setIsListening(true);
      setSpeechError("");
    };

    recognition.onaudiostart = () => {
      console.log("🔊 onaudiostart → le micro reçoit du son.");
    };

    recognition.onaudioend = () => {
      console.log("🎧 onaudioend → fin capture audio.");
    };

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        }
      }

      const clean = finalTranscript.trim();
      if (!clean) return;

      if (autoFollow) {
        console.log("🗣️ Reconnu (autoFollow) → envoi direct :", clean);
        sendToSanta(clean);
      } else {
        console.log("🗣️ Reconnu (manuel) → rempli l'input :", clean);
        setInputText((prev) => (prev ? `${prev.trim()} ${clean}` : clean));
      }
    };

    recognition.onerror = (event) => {
      console.warn("⚠️ SpeechRecognition ERROR:", event.error);

      if (event.error === "no-speech") {
        console.info("ℹ️ Aucun son compris pendant l’écoute (no-speech).");
        return;
      }

      if (event.error === "not-allowed") {
        setSpeechError(
          "Le micro est bloqué pour ce site. Vérifie les autorisations du navigateur."
        );
      } else if (event.error === "audio-capture") {
        setSpeechError(
          "Aucun micro détecté. Vérifie ton micro ou tes paramètres audio."
        );
      } else {
        setSpeechError("Erreur de micro ou de reconnaissance vocale.");
      }

      setIsListening(false);
    };

    recognition.onend = () => {
      console.log("🛑 Micro STOP (onend)");
      setIsListening(false);
    };

    return recognition;
  };

  const handleToggleMic = () => {
    if (isListening) {
      stopListening();
      return;
    }

    console.log("🎤 Activation du micro…");

    const recognition = recognitionRef.current || initSpeechRecognition();

    if (!recognition) {
      alert(
        "Ton navigateur ne permet pas encore de parler directement au Père Noël. Tu peux lui écrire ton message 😊"
      );
      return;
    }

    recognitionRef.current = recognition;
    setSpeechError("");
    try {
      recognition.start();
    } catch (e) {
      console.error("Erreur au démarrage du micro:", e);
      setIsListening(false);
    }
  };

  // ==========================
  //  Envoi message via bouton / Enter
  // ==========================
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || loading || voiceLoading) return;

    await sendToSanta(inputText);
  };

  // ==========================
  //  Effacer le chat
  // ==========================
  const handleClearChat = () => {
    if (window.confirm("Veux-tu vraiment effacer ta lettre au Père Noël ?")) {
      localStorage.removeItem(STORAGE_KEY);

      const resetMessage = [
        {
          sender: "ai",
          content: "Conversation effacée. On recommence à zéro ! Ho ho ho !",
        },
      ];
      setMessages(resetMessage);
    }
  };

  const remainingQuestions =
    santaStatus?.remainingFree != null ? santaStatus.remainingFree : null;

  const maxQuestions =
    santaStatus?.maxMessagesFree != null ? santaStatus.maxMessagesFree : null;

  return (
    <div className="santa-page-container">
      {/* 🎬 OVERLAY INTRO VIDÉO RESPONSIVE */}
      {showIntro && (
        <div className="santa-intro-overlay">
          <div className="santa-intro-wrapper">
            <div className="santa-intro-media">
              <video
                ref={introVideoRef}
                className="santa-intro-video"
                src="/video/Santa_s_Magical_Sleigh_Arrival.mp4"
                autoPlay
                muted
                playsInline
                onEnded={() => setShowIntro(false)}
                onError={(e) =>
                  console.error("❌ Erreur chargement vidéo intro :", e)
                }
              />
            </div>

            <div className="santa-intro-actions">
              <button
                className="santa-intro-btn primary"
                type="button"
                onClick={() => {
                  const video = introVideoRef.current;
                  if (!video) return;
                  video.muted = !video.muted;
                }}
              >
                🔊 Activer / couper le son
              </button>

              <button
                className="santa-intro-btn secondary"
                type="button"
                onClick={() => setShowIntro(false)}
              >
                Passer la vidéo ✨
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Père Noël à gauche */}
      <img
        src="/images/perenoel.png"
        alt="Père Noël"
        className="santa-side-image"
      />

      {/* Colonne centrale : compteur + chat */}
      <div className="santa-center-column">
        {/* ⏰ Compte à rebours */}
        <div className="noel-countdown-wrapper">
          <div className="noel-countdown-image">
            <img
              src="/rebourdnoel.png"
              alt="Compte à rebours avant Noël"
              className="noel-countdown-img"
            />

            <div className="noel-countdown-slot slot-days">
              {countdown.isChristmas ? "0" : countdown.days}
            </div>

            <div className="noel-countdown-slot slot-hours">
              {countdown.isChristmas ? "0" : countdown.hours}
            </div>

            <div className="noel-countdown-slot slot-minutes">
              {countdown.isChristmas ? "0" : countdown.minutes}
            </div>

            <div className="noel-countdown-slot slot-seconds">
              {countdown.isChristmas ? "0" : countdown.seconds}
            </div>
          </div>
        </div>

        {/* BOX DU CHAT */}
        <div className="santa-chat-wrapper">
          {/* HEADER */}
          <div className="santa-chat-header">
            <div className="santa-header-text">
              <h2>Père Noël 🎅</h2>
              <p>En direct du Pôle Nord</p>

              {santaStatus?.isPremium && (
                <div className="santa-premium-badge">
                  ⭐ Compte Premium – magie illimitée
                </div>
              )}
            </div>

            <button
              onClick={handleClearChat}
              className="santa-clear-btn"
              title="Effacer la conversation"
              type="button"
            >
              <Trash2 size={18} />
            </button>
          </div>

          {/* Infos limite / premium */}
          {statusLoading && (
            <div className="santa-status-info">Chargement de la magie… ✨</div>
          )}
          {statusError && (
            <div className="santa-status-error">
              <AlertTriangle size={16} /> {statusError}
            </div>
          )}

          {santaStatus?.isPremium && (
            <div className="santa-status-info premium-info">
              ⭐ Tu as accès à toute la magie du Père Noël, sans limite de
              messages !
            </div>
          )}

          {remainingQuestions !== null && !santaStatus?.isPremium && (
            <div className="guest-limit-info">
              Messages gratuits restants avec le Père Noël :{" "}
              <strong>{remainingQuestions}</strong>
              {maxQuestions ? ` / ${maxQuestions}` : ""}
            </div>
          )}

          {/* ZONE MESSAGES */}
          <div className="santa-messages-area">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`message-bubble ${
                  msg.sender === "ai" ? "santa" : "user"
                }`}
              >
                <div className="message-content">
                  {msg.sender === "ai" && (
                    <Gift size={16} className="message-icon" />
                  )}
                  {msg.content}
                </div>
              </div>
            ))}

            {(loading || voiceLoading) && (
              <div className="message-bubble santa typing">
                <div className="typing-dots">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* ERREUR MICRO */}
          {speechError && (
            <div className="speech-error">
              <AlertTriangle size={16} /> {speechError}
            </div>
          )}

          {/* INPUT FORM + MICRO + AUTO-FOLLOW */}
          <form className="santa-input-area" onSubmit={handleSendMessage}>
            <button
              type="button"
              className={`santa-mic-btn ${isListening ? "listening" : ""}`}
              onClick={handleToggleMic}
              disabled={loading || voiceLoading}
              title={
                isListening
                  ? "Clique pour arrêter le micro"
                  : "Clique pour parler au Père Noël"
              }
            >
              {isListening ? <MicOff size={20} /> : <Mic size={20} />}
            </button>

            <input
              ref={inputRef}
              type="text"
              placeholder={
                autoFollow
                  ? "Tu peux aussi écrire si tu veux…"
                  : "Écris ou dicte ton message..."
              }
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="santa-input"
              disabled={loading || voiceLoading}
            />

            <button
              type="submit"
              className="santa-send-btn"
              disabled={
                loading ||
                voiceLoading ||
                !inputText.trim() ||
                (!santaStatus?.isPremium &&
                  santaStatus?.remainingFree <= 0)
              }
            >
              <Send size={20} />
            </button>
          </form>

          {/* Toggle suivi automatique */}
          <div className="santa-auto-follow">
            <label>
              <input
                type="checkbox"
                checked={autoFollow}
                onChange={(e) => setAutoFollow(e.target.checked)}
              />{" "}
              <strong>Mode mains libres</strong> (réponse et écoute
              automatiques)
            </label>
          </div>

          <div className="santa-footer-decor">
            <Sparkles size={16} /> Magie de Noël NMS06{" "}
            <Sparkles size={16} />
          </div>
        </div>
      </div>

      {/* 🔥 MODAL PLANS QUAND LIMITE ATTEINTE */}
      {showPlanModal && (
        <div className="santa-plan-modal-backdrop">
          <div className="santa-plan-modal">
            <h2>Tu as utilisé tous tes messages gratuits 🎁</h2>
            <p>
              Pour continuer à parler avec le Père Noël et débloquer encore
              plus de magie, demande à un adulte de choisir une formule :
            </p>

            <div className="santa-plan-modal-grid">
              <div className="santa-plan-modal-card">
                <h3>Découverte magique</h3>
                <p className="modal-price">0 €</p>
                <p className="modal-sub">
                  Pour tester tranquillement la discussion.
                </p>
                <button
                  type="button"
                  className="modal-btn-outline"
                  onClick={() => handleSelectPlan("free")}
                >
                  Rester en version gratuite
                </button>
              </div>

              <div className="santa-plan-modal-card best">
                <h3>Famille Magique</h3>
                <p className="modal-price">7,90 €</p>
                <p className="modal-sub">
                  Messages illimités + lettre, chanson et photo-montage.
                </p>
                <button
                  type="button"
                  className="modal-btn-primary"
                  onClick={() => handleSelectPlan("family")}
                >
                  Choisir Famille Magique
                </button>
              </div>

              <div className="santa-plan-modal-card">
                <h3>Renne du Père Noël</h3>
                <p className="modal-price">39,90 €</p>
                <p className="modal-sub">
                  Tout le pack Famille Magique + vidéo personnalisée.
                </p>
                <button
                  type="button"
                  className="modal-btn-secondary"
                  onClick={() => handleSelectPlan("reindeer")}
                >
                  Choisir Renne du Père Noël
                </button>
              </div>
            </div>

            <button
              type="button"
              className="modal-close-link"
              onClick={() => setShowPlanModal(false)}
            >
              Continuer plus tard et fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
